import React, { useEffect, useMemo, useState } from 'react';

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const LineItemAdjustModal = ({ item, discountReasons = [], canApplyDiscount = false, onClose, onApply }) => {
  const baseUnitPrice = Number.isFinite(item?.basePrice) ? item.basePrice : item?.price || 0;
  const quantity = item?.quantity || 1;
  const baseLineTotal = baseUnitPrice * quantity;

  const [mode, setMode] = useState(() => {
    if (item?.discountType === 'percent' && canApplyDiscount) return 'percent';
    if (item?.discountType === 'amount' && canApplyDiscount) return 'amount';
    if (item?.overridePrice != null) return 'override';
    return canApplyDiscount ? 'percent' : 'override';
  });
  const [percentValue, setPercentValue] = useState(
    item?.discountType === 'percent' ? String(item.discountValue ?? '') : ''
  );
  const [amountValue, setAmountValue] = useState(
    item?.discountType === 'amount' ? String(item.discountValue ?? '') : ''
  );
  const [overrideValue, setOverrideValue] = useState(
    item?.overridePrice != null ? String(item.overridePrice) : ''
  );
  const [discountReasonId, setDiscountReasonId] = useState(item?.discountReasonId || '');

  useEffect(() => {
    if (!canApplyDiscount && (mode === 'percent' || mode === 'amount')) {
      setMode('override');
    }
  }, [canApplyDiscount, mode]);

  const summary = useMemo(() => {
    let discountAmount = 0;
    let adjustedTotal = baseLineTotal;
    if (mode === 'percent') {
      const percent = clamp(toNumber(percentValue), 0, 100);
      discountAmount = (baseLineTotal * percent) / 100;
      adjustedTotal = baseLineTotal - discountAmount;
    } else if (mode === 'amount') {
      const perItemDiscount = clamp(toNumber(amountValue), 0, baseUnitPrice);
      discountAmount = clamp(perItemDiscount * quantity, 0, baseLineTotal);
      adjustedTotal = baseLineTotal - discountAmount;
    } else if (mode === 'override') {
      if (overrideValue !== '') {
        const overridePrice = clamp(toNumber(overrideValue), 0, Number.MAX_SAFE_INTEGER);
        adjustedTotal = overridePrice * quantity;
        discountAmount = Math.max(0, baseLineTotal - adjustedTotal);
      }
    }
    const savings = Math.max(0, baseLineTotal - adjustedTotal);
    return { discountAmount, adjustedTotal, savings };
  }, [mode, percentValue, amountValue, overrideValue, baseLineTotal, baseUnitPrice, quantity]);

  const handleApply = () => {
    if (mode === 'percent') {
      const percent = clamp(toNumber(percentValue), 0, 100);
      if (percent <= 0) {
        onApply({ mode: 'none' });
        return;
      }
      onApply({ mode: 'percent', discountValue: percent, discountReasonId });
      return;
    }
    if (mode === 'amount') {
      const amount = clamp(toNumber(amountValue), 0, baseUnitPrice);
      if (amount <= 0) {
        onApply({ mode: 'none' });
        return;
      }
      onApply({ mode: 'amount', discountValue: amount, discountReasonId });
      return;
    }
    if (mode === 'override') {
      const overridePrice = clamp(toNumber(overrideValue), 0, Number.MAX_SAFE_INTEGER);
      if (!Number.isFinite(overridePrice) || overridePrice <= 0) {
        onApply({ mode: 'none' });
        return;
      }
      onApply({ mode: 'override', overridePrice, discountReasonId });
      return;
    }
    onApply({ mode: 'none' });
  };

  if (!item) return null;

  const requiresReason = (mode === 'percent' && clamp(toNumber(percentValue), 0, 100) > 0)
    || (mode === 'amount' && clamp(toNumber(amountValue), 0, baseUnitPrice) > 0)
    || (mode === 'override' && summary.savings > 0);
  const requiresDiscountPermission = requiresReason;
  const activeReasons = discountReasons.filter((reason) => reason?.id && reason?.name);
  const isApplyDisabled = requiresReason && (!canApplyDiscount || !discountReasonId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl bg-white p-4 sm:p-6 shadow-xl space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Adjust item</h3>
            <p className="text-xs text-slate-500">
              {item.name} • Qty {quantity} • ${baseLineTotal.toFixed(2)}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="space-y-3">
          {canApplyDiscount && (
            <>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="adjustmentMode"
                  checked={mode === 'percent'}
                  onChange={() => setMode('percent')}
                />
                Discount (%)
              </label>
              {mode === 'percent' && (
                <div className="ml-6 space-y-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={percentValue}
                    onChange={(e) => setPercentValue(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Enter percentage"
                  />
                  <div className="flex flex-wrap gap-2">
                    {[5, 10, 15, 20].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setPercentValue(String(value))}
                        className="px-3 py-1 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-100"
                      >
                        {value}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="adjustmentMode"
                  checked={mode === 'amount'}
                  onChange={() => setMode('amount')}
                />
                Discount ($)
              </label>
              {mode === 'amount' && (
                <div className="ml-6">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountValue}
                    onChange={(e) => setAmountValue(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Enter per-item discount"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Discount applies per item.</p>
                </div>
              )}
            </>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="adjustmentMode"
              checked={mode === 'override'}
              onChange={() => setMode('override')}
            />
            Override unit price
          </label>
          {mode === 'override' && (
            <div className="ml-6 space-y-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={overrideValue}
                onChange={(e) => setOverrideValue(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                placeholder="Enter new unit price"
              />
              {requiresDiscountPermission && !canApplyDiscount && (
                <p className="text-xs text-rose-600">
                  Your role cannot apply item discounts. Ask a manager with discount permission.
                </p>
              )}
            </div>
          )}

          {requiresReason && canApplyDiscount && (
            <div className="ml-6 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              <label htmlFor="discount-reason" className="block text-sm font-medium text-slate-800">
                Discount reason
              </label>
              <select
                id="discount-reason"
                value={discountReasonId}
                onChange={(event) => setDiscountReasonId(event.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select a reason</option>
                {activeReasons.map((reason) => (
                  <option key={reason.id} value={reason.id}>{reason.name}</option>
                ))}
              </select>
              <p className="text-xs text-amber-800">
                {activeReasons.length
                  ? 'Select a reason before applying this staff discount.'
                  : 'No active discount reasons are configured. Ask an administrator to add one in Settings.'}
              </p>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 space-y-1">
          <div className="flex justify-between">
            <span>Adjusted total</span>
            <span className="font-semibold">${summary.adjustedTotal.toFixed(2)}</span>
          </div>
          {summary.savings > 0 && (
            <div className="flex justify-between text-emerald-600">
              <span>You save</span>
              <span>- ${summary.savings.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onApply({ mode: 'none' })}
            className="px-4 py-2 rounded-md border border-slate-300 text-slate-600"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-slate-300 text-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={isApplyDisabled}
            className="px-4 py-2 rounded-md bg-slate-900 text-white disabled:bg-slate-400 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineItemAdjustModal;
